import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { Observable, firstValueFrom, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseDashboard } from '../../models/dashboard/BaseDashboard';
import { FormSearch } from '../../models/dashboard/FormStatus';
import { UtilityService } from '../utility.service';
import { EntriesView } from 'app/models/general-ledger/EntriesView';
import { ProjectGrantByAccountsResultItem } from '../../models/projects-grants/ProjectGrantByAccountsBatch';
import { BatchCacheLoader } from '../../shared/utils/batch-cache-loader';
import { DynamicFormUtilityServices } from '../dynamic-form-utility.service';

@Injectable({
	providedIn: 'root',
})
export class ProjectGrantFormService {
	private readonly urlPath = `${environment.apiUrl}project-grant/clients/`;
	headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0' });

	// Per (clientId, isActive) DataLoader that coalesces same-tick project-grant-by-account lookups into
	// one batch request and memoizes by account for the page lifecycle. Project-grant reference data is
	// stable within an editing session, so memoizing is safe and collapses the per-row N+1.
	private readonly projectGrantLoaders = new Map<string, BatchCacheLoader<unknown[]>>();

	constructor(
		private httpClient: HttpClient,
		private utilityService: UtilityService,
		private dynamicFormUtilityServices: DynamicFormUtilityServices
	) {}

	private getProjectGrantLoader(clientId: string, isActive: boolean | null): BatchCacheLoader<unknown[]> {
		const scope = `${clientId}|${isActive}`;
		let loader = this.projectGrantLoaders.get(scope);
		if (!loader) {
			loader = new BatchCacheLoader<unknown[]>(
				(accounts) =>
					firstValueFrom(
						this.getProjectAndGrantByAccountsBatch(clientId, accounts, isActive).pipe(
							map((items) => new Map(items.map((item) => [item.account, item.data])))
						)
					),
				// A batch omission resolves like the single-item endpoint's "not found": empty.
				{ defaultForMissingKey: () => [] }
			);
			this.projectGrantLoaders.set(scope, loader);
		}
		return loader;
	}

	/** Drop the memoized project-grant lookups (e.g. on client switch or after config edits). */
	clearProjectGrantLookupCache(): void {
		this.projectGrantLoaders.forEach((loader) => loader.clear());
		this.projectGrantLoaders.clear();
	}

	createForm(clientId: string, request: any, prefix: string = ''): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/project-grant-form`, this.setupRequest(request, prefix))
			.pipe(tap(() => this.clearProjectGrantLookupCache()));
	}

	private setupRequest(request: any, prefix: string = '') {
		const formData: FormData = new FormData();

		formData.append('model', JSON.stringify(request));
		for (let i = 0; i < request.attachments.length; i++) {
			const name = `Other-${i + 1}`;
			formData.append(name, request.attachments[i].file);
			request.attachments[i].name = name;
		}
		for (let i = 0; i < request.grantAgreement.length; i++) {
			const name = `Grant Agreement-${i + 1}`;
			formData.append(name, request.grantAgreement[i].file);
			request.grantAgreement[i].name = name;
		}
		for (let i = 0; i < request.grantApplication.length; i++) {
			const name = `Grant Application-${i + 1}`;
			formData.append(name, request.grantApplication[i].file);
			request.grantApplication[i].name = name;
		}
		for (let i = 0; i < request.grantAmendments?.length; i++) {
			const name = `Grant Amendments-${i + 1}`;
			formData.append(name, request.grantAmendments[i].file);
			request.grantAmendments[i].name = name;
		}
		for (let i = 0; i < request.importedFiles?.length; i++) {
			const name = `Imported File-${i + 1}`;
			formData.append(name, request.importedFiles[i]);
		}

		if (request.additionalField?.dynamicFormConfigurations) {
			this.dynamicFormUtilityServices.appendDocuments(formData, prefix, request);
		}
		return formData;
	}

	updateForm(clientId: string, id: string, request: any, prefix: string = ''): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/project-grant-form/${id}`, this.setupRequest(request, prefix))
			.pipe(tap(() => this.clearProjectGrantLookupCache()));
	}

	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/project-grant-form/${id}`).pipe();
	}
	getFormWithExpensesRevenue(clientId: string, id: string, year: number, month: number): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/project-grant-form/${id}/${year}/${month}`).pipe();
	}

	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/project-grant-form/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		// Approval makes a project-grant visible to the by-account lookups.
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/project-grant-form/${id}/approve`, {
				userId,
			})
			.pipe(tap(() => this.clearProjectGrantLookupCache()));
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${this.urlPath}${clientId}/project-grant-form/users/${userId}?${this.utilityService.objectToQueryString(
			search
		)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/project-grant-form/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/project-grant-form/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/project-grant-form/${formId}/docs/${docId}`);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/project-grant-form/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/project-grant-form/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	getAll(clientId: string, type: string, isClosed: boolean): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/project-grant-form/get-all/${type}/${isClosed}`, {
				headers: this.headers,
			})
			.pipe();
	}

	getAllWithYearlyBudgets(clientId: string, type: string, isClosed: boolean): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/project-grant-form/get-all-with-yearly-budgets/${type}/${isClosed}`, {
				headers: this.headers,
			})
			.pipe();
	}
	getAllWithoutType(clientId: string, isClosed: boolean): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/project-grant-form/get-all-without-type/${isClosed}`, {
				headers: this.headers,
			})
			.pipe();
	}

	getProjectAndGrantByAccount(clientId: string, account: string, isActive?: boolean | null): Observable<any[]> {
		if (!account || account.trim() === '') {
			return of([]);
		}
		const normalizedAccount = account.trim();
		const actualIsActive: boolean | null = isActive === null ? null : isActive === undefined ? true : isActive;
		// Routed through the batch+cache loader: identical/same-tick lookups coalesce to a single
		// get-all-by-accounts request and repeats are served from cache. Result is byte-identical to the
		// old per-account GET (the loader maps the account's `data` back to this caller).
		return this.getProjectGrantLoader(clientId, actualIsActive)
			.load$(normalizedAccount)
			.pipe(map((data) => (data as any[]) ?? []));
	}

	// Batch of getProjectAndGrantByAccount: resolves project-grants for many GL accounts in one
	// request. isActive applies to the whole batch (matches the single-item semantics); results are
	// keyed by account so the caller maps them per row.
	getProjectAndGrantByAccountsBatch(
		clientId: string,
		accounts: string[],
		isActive?: boolean | null
	): Observable<ProjectGrantByAccountsResultItem[]> {
		if (!accounts || accounts.length === 0) {
			return of([]);
		}
		const actualIsActive: boolean | null = isActive === null ? null : isActive === undefined ? true : isActive;
		let url = `${this.urlPath}${clientId}/project-grant-form/get-all-by-accounts`;
		if (actualIsActive !== null) {
			url += `?isActive=${actualIsActive}`;
		}
		return this.httpClient.post<ProjectGrantByAccountsResultItem[]>(url, accounts).pipe();
	}

	getRollforward(clientId: string, type: string, year: number, month: number): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/project-grant-form/rollforward/${type}/${year}/${month}`)
			.pipe();
	}
	changeAccountStatus(clientId: string, formId: any, selectedIds: any[], status: boolean): Observable<any> {
		let queryParams = new HttpParams();
		selectedIds.forEach((id) => {
			queryParams = queryParams.append('selectedIds', id.toString());
		});

		return this.httpClient
			.post<any>(
				`${this.urlPath}${clientId}/project-grant-form/changeAccountStatus/${formId}/${status}`,
				{},
				{ params: queryParams }
			)
			.pipe();
	}

	validateAccountsByPGAndTasks(clientId: string, entries: EntriesView[]): Observable<any> {
		const formData: FormData = new FormData();
		formData.append('model', JSON.stringify(entries));
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/project-grant-form/validateAccountsByPGAndTasks/`, formData)
			.pipe();
	}
	getProjectAndGrantWithYearlyBudgetsByAccount(
		clientId: string,
		account: string,
		isActive?: boolean | null
	): Observable<any[]> {
		const actualIsActive: boolean | null = isActive === null ? null : isActive === undefined ? true : isActive;
		const url = `${this.urlPath}${clientId}/project-grant-form/get-all-with-yearly-budgets-by-account/${account}/${actualIsActive}`;
		return this.httpClient.get<any[]>(url).pipe();
	}
	getProjectAndGrantWithYearlyBudgetsByAccounts(clientId: string, gls: string[]): Observable<any[]> {
		const url = `${this.urlPath}${clientId}/project-grant-form/get-all-with-yearly-budgets-by-accounts`;
		return this.httpClient.post<any[]>(url, gls).pipe();
	}
	importProjectGrant(clientId: string, data: any, isPGOnly: boolean): Observable<any> {
		return this.httpClient
			.post(`${this.urlPath}${clientId}/project-grant-form/import-project-grant?isPGOnly=${isPGOnly}`, data)
			.pipe();
	}
	checkImportStatus(clientId: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.urlPath}${clientId}/project-grant-form/import-status`);
	}
	getIntegrationExpenses(
		clientId: string,
		projectGrantFormId: string,
		projectGrantTaskId?: string | null,
		year?: number | null,
		month?: number | null
	): Observable<any[]> {
		let params = new HttpParams();
		if (projectGrantTaskId) {
			params = params.append('projectGrantTaskId', projectGrantTaskId);
		}
		if (year) {
			params = params.append('year', year.toString());
		}
		if (month) {
			params = params.append('month', month.toString());
		}
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/project-grant-form/${projectGrantFormId}/integration-expenses`, {
				params,
			})
			.pipe();
	}

	getSubrecipientsFundingSourceReport(clientId: string, type: string, year: number): Observable<any[]> {
		return this.httpClient
			.get<any[]>(
				`${this.urlPath}${clientId}/project-grant-form/subrecipients-funding-source-details-report/${type}/${year}`
			)
			.pipe();
	}
}
